import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  CircularProgress,
  Skeleton,
  Divider,
  alpha,
  Backdrop
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import QuizIcon from '@mui/icons-material/Quiz';
import BarChartIcon from '@mui/icons-material/BarChart';
import DateRangeIcon from '@mui/icons-material/DateRange';
import KpiCard from './KpiCard';
import FaqTable from './FaqTable';
import DateRangePicker from './DateRangePicker';

// Color palette that matches KPI cards and table
const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  purple: '#673ab7',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
  hoverRow: alpha('#2196f3', 0.08)
};

const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: COLORS.background,
  minHeight: '100vh',
  borderRadius: theme.shape.borderRadius,
}));

const DashboardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  '& h1': {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.75rem',
    color: '#424242',
    fontWeight: 600,
    '& svg': {
      marginRight: theme.spacing(1.5),
      color: COLORS.primary,
    }
  }
}));

const DashboardPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  border: 'none',
  boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)',
  marginBottom: theme.spacing(3),
  backgroundColor: '#FFFFFF',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  color: '#424242',
  marginBottom: theme.spacing(2.5),
  '& svg': {
    marginRight: theme.spacing(1),
    color: COLORS.primary,
  }
}));

const quickRanges = [
  { 
    label: 'Semana a la fecha', 
    type: 'week_to_date',
    getDateRange: () => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(today);
      monday.setDate(today.getDate() - mondayOffset);
      monday.setHours(0, 0, 0, 0);
      
      return {
        start_date: monday.toISOString().slice(0, 10),
        end_date: today.toISOString().slice(0, 10)
      };
    }
  },
  { 
    label: 'Mes a la fecha', 
    type: 'month_to_date',
    getDateRange: () => {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      
      return {
        start_date: firstDayOfMonth.toISOString().slice(0, 10),
        end_date: today.toISOString().slice(0, 10)
      };
    }
  },
  { label: 'Últimos 7 días', days: 7 },
  { label: 'Últimos 14 días', days: 14 },
  { label: 'Últimos 30 días', days: 30 },
];

function DashboardSkeleton() {
  return (
    <Box sx={{ width: '100%' }}>
      {/* Title Skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={300} height={40} />
        </Box>
        <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 1 }} />
      </Box>
      
      {/* KPI Cards Skeleton */}
      <Grid container spacing={3} mb={4}>
        {[...Array(4)].map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{ p: 2, height: '100%', borderRadius: 1, background: alpha('#f5f5f5', 0.5) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1.5 }} />
                <Skeleton variant="text" width="60%" height={30} />
              </Box>
              <Skeleton variant="text" width="40%" height={60} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={24} />
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      {/* Table Skeleton */}
      <DashboardPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={28} height={28} sx={{ mr: 1.5 }} />
          <Skeleton variant="text" width={250} height={32} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 1 }} />
      </DashboardPaper>

      {/* Metrics Skeleton */}
      <DashboardPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={28} height={28} sx={{ mr: 1.5 }} />
          <Skeleton variant="text" width={250} height={32} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 1 }} />
      </DashboardPaper>
    </Box>
  );
}

function FaqDashboard(props) {
  const { summary, loading, errorMessage, getFaqDashboardSummary, profile, sucursal_id } = props;
  const [selectedQuickRange, setSelectedQuickRange] = useState(4);
  const [manualRange, setManualRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);
    return [start, end];
  });

  useEffect(() => {
    if (selectedQuickRange !== null) {
      const range = quickRanges[selectedQuickRange];
      if (range.type === 'week_to_date' || range.type === 'month_to_date') {
        const dateRange = range.getDateRange();
        getFaqDashboardSummary(dateRange, profile.tenant_id, sucursal_id);
      } else {
        getFaqDashboardSummary(range.days, profile.tenant_id, sucursal_id);
      }
    }
  }, [selectedQuickRange, getFaqDashboardSummary, profile.tenant_id, sucursal_id]);

  const handleQuickRangeChange = (idx, range) => {
    setSelectedQuickRange(idx);
    setManualRange([null, null]);
  };

  const handleManualRangeChange = (range) => {
    if (range[0] && range[1]) {
      setManualRange([range[0], range[1]]);
      setSelectedQuickRange(null);
      getFaqDashboardSummary(
        {
          start_date: range[0] instanceof Date ? range[0].toISOString().slice(0, 10) : range[0],
          end_date: range[1] instanceof Date ? range[1].toISOString().slice(0, 10) : range[1],
        },
        profile.tenant_id,
        sucursal_id
      );
    }
  };

  if (loading && (!summary || !summary.cards || summary.cards.length === 0)) {
    return (
      <DashboardContainer>
        <DashboardSkeleton />
      </DashboardContainer>
    );
  }

  if (errorMessage) {
    return (
      <DashboardContainer>
        <Box sx={{ p: 3, backgroundColor: '#ffebee', borderRadius: 1 }}>
          <Typography color="error">Error: {errorMessage}</Typography>
        </Box>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <Typography variant="h4" component="h1">
          <DashboardIcon fontSize="large" />
          Dashboard Preguntas Frecuentes
        </Typography>
        
        <DateRangePicker
          value={manualRange}
          onChange={handleManualRangeChange}
          quickRanges={quickRanges}
          selectedQuickRange={selectedQuickRange}
          onQuickRangeChange={handleQuickRangeChange}
          loading={loading}
        />
      </DashboardHeader>

      {/* KPI Cards */}
      <Grid container spacing={3} mb={4}>
        {loading
          ? [...Array(5)].map((_, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Paper sx={{ p: 2, height: '100%', borderRadius: 1, background: alpha('#f5f5f5', 0.5) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1.5 }} />
                    <Skeleton variant="text" width="60%" height={30} />
                  </Box>
                  <Skeleton variant="text" width="40%" height={60} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="80%" height={24} />
                </Paper>
              </Grid>
            ))
          : summary && summary.cards && summary.cards.map((card, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <KpiCard
                  title={card.title}
                  value={card.value}
                  unit={card.unit}
                  description={card.description}
                  change={card.change}
                />
              </Grid>
            ))}
      </Grid>

      {/* Metrics */}
      <DashboardPaper>
        <SectionTitle variant="h5" component="h2">
          <DateRangeIcon />
          Métricas del Período
        </SectionTitle>

        {summary?.metrics && !loading ? (
          <>
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                mb: 3,
                p: 2,
                backgroundColor: alpha(COLORS.primary, 0.05),
                borderRadius: 1,
                border: `1px solid ${alpha(COLORS.primary, 0.1)}`,
              }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                <strong>Total Conversaciones:</strong>{' '}
                {summary.metrics.conversations.reduce((a, b) => a + b, 0)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                <strong>Total Usuarios:</strong>{' '}
                {summary.metrics.users.reduce((a, b) => a + b, 0)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}>
              <LineChart
                xAxis={[
                  {
                    scaleType: 'band',
                    data: summary.metrics.x_axis,
                    label: 'Fecha',
                    labelStyle: { fontSize: 16, transform: 'translateY(20px)' },
                    tickLabelStyle: { fontSize: 12, angle: -45, textAnchor: 'end' },
                  },
                ]}
                yAxis={[
                  {
                    label: 'Registros',
                    tickMinStep: 1,
                    labelStyle: {
                      fontSize: 16
                    },
                    tickLabelStyle: {
                      fontSize: 12
                    }
                  },
                ]}
                series={[
                  {
                    data: summary.metrics.conversations,
                    label: 'Conversaciones',
                    color: '#ff69b4'
                  },
                  {
                    data: summary.metrics.users,
                    label: 'Usuarios',
                  },
                ]}
                width={1000}
                height={360}
                margin={{ bottom: 60 }}
              />
            </Box>
          </>
        ) : loading ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Skeleton variant="circular" width={28} height={28} sx={{ mr: 1.5 }} />
              <Skeleton variant="text" width={250} height={32} />
            </Box>
            <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 1 }} />
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary' }}>
            <BarChartIcon sx={{ fontSize: 40, color: alpha(COLORS.primary, 0.3), mb: 1 }} />
            <Typography>No hay métricas disponibles</Typography>
          </Box>
        )}
      </DashboardPaper>

      {/* FAQ Table */}
      <DashboardPaper>
        <SectionTitle variant="h5" component="h2">
          <QuizIcon />
          Estadísticas por Pregunta Frecuente
        </SectionTitle>
        {loading ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Skeleton variant="circular" width={28} height={28} sx={{ mr: 1.5 }} />
              <Skeleton variant="text" width={250} height={32} />
            </Box>
            <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 1 }} />
          </>
        ) : (
          <FaqTable 
            data={summary && summary.faq_table ? summary.faq_table : []} 
          />
        )}
      </DashboardPaper>
    </DashboardContainer>
  );
}

export default FaqDashboard; 